import apiInstance from "api";
import { clearRefreshToken, storeRefreshToken } from "utils/session";
import {
  getUserFailure,
  getUserStart,
  getUserSuccess,
  loginUserFailure,
  loginUserStart,
  loginUserSuccess,
  loginWithEmailFailure,
  loginWithEmailStart,
  loginWithEmailSuccess,
  logoutUserAction,
  overrideSubdomainAction,
  tokenAction,
  twoFactorRequired,
} from "./reducers";
import { clearTeamPromptDismissals } from "lcp/utils/teamPromptDismissal";
import { clearSharedAuthToken } from "../../utils/sharedAuthCookie";
import { clearSsoSession, endSessionUrl, followSessionLink } from "auth/oidc";
import { markFullSignIn } from "utils/quickSignIn";

const AUTH_ROLE_KEY = "authRoleName";

const readRoleName = (payload) =>
  payload?.userDetails?.userDetails?.role
  ?? payload?.userDetails?.role
  ?? payload?.userDetails?.roleName
  ?? payload?.userDetails?.userProfile?.roleName
  ?? payload?.userDetails?.userProfile?.role
  ?? payload?.roleName
  ?? payload?.role
  ?? null;

const persistRoleName = (payload) => {
  const roleName = readRoleName(payload);
  if (roleName) {
    localStorage.setItem(AUTH_ROLE_KEY, roleName);
  } else {
    localStorage.removeItem(AUTH_ROLE_KEY);
  }
};

// Stores a sign-in envelope ({ accessToken, refreshToken, userDetails }) however it
// was got: password, code, emailed link, set-password link or device PIN. With single
// sign-on on, the envelope is { next } instead: the link that starts the Keycloak
// session, after which /auth/callback stores the envelope (auth/oidc).
export const applySignIn = (result) => {
  return (dispatch) => {
    if (result?.next) {
      followSessionLink(result.next, result.subject, result.claim);
      return;
    }
    if (result?.userDetails?.overrideSubdomain) {
      dispatch(overrideSubdomainAction(result?.userDetails?.overrideSubdomain));
    }

    setTimeout(function () {
      dispatch(loginUserSuccess(result));
      persistRoleName(result);

      if (result?.userDetails?.overrideSubdomain) {
        dispatch(overrideSubdomainAction(result?.userDetails?.overrideSubdomain));
      }

      // Two-step verification happens on the server before it sends a token, so
      // any token that arrives here is a finished sign-in.
      dispatch(tokenAction(result.accessToken));
      // After tokenAction, which clears any previous session's one.
      storeRefreshToken(result.refreshToken, result.refreshExpiresIn);
    }, 500);
  };
};

// `path` is the sign-in endpoint: /auth/login (password or code) by default;
// /auth/login/link/redeem, /auth/set-password and /auth/pin/login answer with the
// same envelope.
export const loginUser = (data, path = "/auth/login") => {
  return async (dispatch) => {
    try {
      dispatch(logoutUser());
      dispatch(loginUserStart());
      const response = await apiInstance.post(path, data);
      const result = response?.data?.data;
      if (result?.twoFactorRequired) {
        // Password (or link, or emailed code) accepted; the server now wants the
        // second code. The sign-in page shows that step (pages/Login).
        dispatch(twoFactorRequired({ ...result, message: response?.data?.message }));
        return;
      }
      if (result?.next) {
        // Single sign-on: Keycloak starts the session, /auth/callback finishes. The
        // page unloads, so a password or code sign-in marks itself for the PIN offer
        // now (pages/Login does it when the token arrives).
        if (path === "/auth/login") markFullSignIn();
        followSessionLink(result.next, result.subject, result.claim);
        return;
      }
      if (!result?.accessToken) {
        // Answered, but no session (e.g. "Password set. Sign in with it.").
        dispatch(loginUserFailure({ message: response?.data?.message || "Unable to sign in.", data: result }));
        return;
      }
      dispatch(applySignIn(result));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(loginUserFailure(error.response.data));
      } else {
        dispatch(loginUserFailure(error?.message || "Something went wrong"));
      }
    }
  };
};

export const logoutUser = (data) => {
  return async (dispatch) => {
    try {
      localStorage.removeItem("authToken");
      // Also the parent-domain copy, or the next page load seeds it
      // straight back and the sign-out appears not to take.
      clearSharedAuthToken();
      clearRefreshToken();
      // Only this host's copy: the Keycloak session itself ends in signOut().
      clearSsoSession();
      if (localStorage.getItem("_subdomain")) {
        localStorage.removeItem("_subdomain");
      }
      localStorage.removeItem(AUTH_ROLE_KEY);
      // Drop the super-admin acting-org (stored in sessionStorage) too — otherwise
      // it survives logout and the next login sends X-Acting-Org-Id for a tenant
      // the new subject may not have access to, stalling every request.
      try { sessionStorage.removeItem("abacActingOrgId"); } catch (_) {}
      // The assets list's remembered location scope is keyed per user, so the
      // next subject starts from their own; nothing to clear here (Jam 11eb2447).
      // "Don't ask me again" on the inspection-team prompt is a this-session
      // answer. The next subject has declined nothing.
      clearTeamPromptDismissals();
      // Lift Connect Pro pages remember their lift scope (lcp.inspections.scope,
      // lcp.icc.scope, lcp.inspectionResults.scope, ...). A scope that matched
      // nothing used to survive sign-out, so logging out and back in landed
      // the user on the same empty page.
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("lcp.") && k.endsWith(".scope"))
          .forEach((k) => localStorage.removeItem(k));
      } catch (_) {}
      dispatch(logoutUserAction({}));
    } catch (error) {}
  };
};

// The user's own "Logout": this host's state, then, for a single sign-on session,
// Keycloak's, which signs every other F2 host out at its next renewal.
export const signOut = () => {
  return async (dispatch) => {
    const keycloakSignOut = endSessionUrl();
    await dispatch(logoutUser());
    if (keycloakSignOut) window.location.assign(keycloakSignOut);
  };
};

export const getUser = (data) => {
  return async (dispatch) => {
    try {
      dispatch(getUserStart());
      // Always send a JSON body. The server's `[FromBody] IntrospectRequest`
      // binding returns 415 Unsupported Media Type when the request has no
      // body — even with Content-Type: application/json on the headers.
      // Callers that have nothing meaningful to send (e.g. the App.js
      // rehydration on mount, or the picker/sidebar refresh) get an empty
      // object; the server reads identity from the JWT claims, not the body.
      const response = await apiInstance.post("/auth/user-info", data ?? {});
      persistRoleName(response.data.data);
      dispatch(getUserSuccess(response.data.data));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(getUserFailure(error.response.data));
      } else {
        dispatch(getUserFailure(error?.message || "Something went wrong"));
      }
    }
  };
};

export const sendLoginWithEmailLink = (data) => {
  return async (dispatch) => {
    try {
      dispatch(loginWithEmailStart());
      // A one-time sign-in link; the answer is the same whether or not the account exists.
      const response = await apiInstance.post("/auth/login/link", { identifier: data?.identifier ?? data?.Email ?? data?.email });
      dispatch(loginWithEmailSuccess(response.data.data));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(loginWithEmailFailure(error.response.data));
      } else {
        dispatch(loginWithEmailFailure(error?.message || "Something went wrong"));
      }
    }
  };
};
